// src/app/shared/system-roles.ts
// ⚠️ Keep in sync with ElikitaAuth.API.Models.SystemRoles (C#)

export const SystemRoles = {
  // Super Admin - can manage everything across all organizations
  SuperAdmin: 'superadmin',

  // Organization Admin - can manage users within their organization
  // (business name: "Hospital Admin")
  HospitalAdmin: 'hospitaladmin',

  // Generic Practitioner (used during registration, before specific role assignment)
  Practitioner: 'practitioner',

  // Specific Practitioner Roles (assigned during approval)
  AlliedHealthProfessional: 'alliedhealthprofessional',
  Doctor: 'doctor',
  HealthcareAssistant: 'healthcareassistant',
  LabTechnician: 'labtechnician',
  Pharmacist: 'pharmacist',
  HealthcareAdmin: 'healthcareadmin',
  RemoteDoctor: 'remotedoctor',
  Cashier: 'cashier',

  PrimaryHealthcareProvider: 'primaryhealthcareprovider',
  NurseMidwife: 'nursemidwife',
  EventManager: 'eventmanager',

  // Patient Role
  Patient: 'patient'
} as const;

// Type-safe union of all role strings
export type RoleType = typeof SystemRoles[keyof typeof SystemRoles];

// Arrays for validation - MUST match C# backend
export const PRACTITIONER_ROLES = [
  SystemRoles.SuperAdmin,
  SystemRoles.HospitalAdmin,
  SystemRoles.Practitioner,
  SystemRoles.AlliedHealthProfessional,
  SystemRoles.Doctor,
  SystemRoles.HealthcareAssistant,
  SystemRoles.LabTechnician,
  SystemRoles.Pharmacist,
  SystemRoles.HealthcareAdmin,
  SystemRoles.RemoteDoctor,
  SystemRoles.Cashier,
  SystemRoles.PrimaryHealthcareProvider,
  SystemRoles.NurseMidwife,
  SystemRoles.EventManager
] as const;

export const PATIENT_ROLES = [SystemRoles.Patient] as const;

// 🔥 FIX: Create a type that only includes practitioner roles
export type PractitionerRoleType = typeof PRACTITIONER_ROLES[number];

// Alias lookup table mapping alternative/legacy role names to canonical SystemRoles keys
const ROLE_ALIAS_MAP: Record<string, string> = {
  'admin': SystemRoles.HospitalAdmin,
  'hospital admin': SystemRoles.HospitalAdmin,
  'hospital_admin': SystemRoles.HospitalAdmin,
  'receptionist': SystemRoles.HealthcareAdmin,
  'healthcare admin': SystemRoles.HealthcareAdmin,
  'healthcare_admin': SystemRoles.HealthcareAdmin,
  'super admin': SystemRoles.SuperAdmin,
  'super_admin': SystemRoles.SuperAdmin,
  'nurse': SystemRoles.NurseMidwife,
  'consultant': SystemRoles.AlliedHealthProfessional
};

/**
 * Normalizes any role string (legacy, formatted, or standard) to its canonical SystemRoles key.
 */
export function normalizeRole(role: string | null | undefined): string {
  if (!role) return '';
  const cleaned = role.trim().toLowerCase().replace(/\s+/g, '');
  if (Object.values(SystemRoles).includes(cleaned as any)) {
    return cleaned;
  }
  const rawClean = role.trim().toLowerCase();
  return ROLE_ALIAS_MAP[rawClean] || ROLE_ALIAS_MAP[cleaned] || cleaned;
}

/**
 * Shared helper function to determine whether a person is a Hospital Admin
 */
export function isHospitalAdmin(role: string | null | undefined): boolean {
  return normalizeRole(role) === SystemRoles.HospitalAdmin;
}

/**
 * Shared helper function to determine whether a person is a Healthcare Admin (Receptionist)
 */
export function isHealthcareAdmin(role: string | null | undefined): boolean {
  return normalizeRole(role) === SystemRoles.HealthcareAdmin;
}

// Helper functions
export function isPractitionerRole(role: string | null | undefined): boolean {
  const norm = normalizeRole(role);
  return PRACTITIONER_ROLES.includes(norm as any);
}

export function isPatientRole(role: string | null | undefined): boolean {
  return normalizeRole(role) === SystemRoles.Patient;
}


// For UI display mapping
export const ROLE_DISPLAY_NAMES: Record<RoleType, string> = {
  [SystemRoles.SuperAdmin]: 'Super Admin',
  [SystemRoles.HospitalAdmin]: 'Hospital Admin',
  [SystemRoles.Practitioner]: 'Practitioner',
  [SystemRoles.AlliedHealthProfessional]: 'Allied Health Professional',
  [SystemRoles.Doctor]: 'Doctor',
  [SystemRoles.HealthcareAssistant]: 'Healthcare Assistant',
  [SystemRoles.LabTechnician]: 'Lab Technician',
  [SystemRoles.Pharmacist]: 'Pharmacy Technician',
  [SystemRoles.HealthcareAdmin]: 'Healthcare Admin',
  [SystemRoles.RemoteDoctor]: 'Remote Doctor',
  [SystemRoles.Cashier]: 'Cashier',
  [SystemRoles.Patient]: 'Patient',
  [SystemRoles.PrimaryHealthcareProvider]: 'Primary Healthcare Provider',
  [SystemRoles.NurseMidwife]: 'Nurse / Midwife',
  [SystemRoles.EventManager]: 'Event Manager',
};

// For dropdowns / selection dialogs
export const AVAILABLE_PRACTITIONER_ROLE_OPTIONS = [
  { code: SystemRoles.AlliedHealthProfessional, display: ROLE_DISPLAY_NAMES[SystemRoles.AlliedHealthProfessional] },
  { code: SystemRoles.Doctor, display: ROLE_DISPLAY_NAMES[SystemRoles.Doctor] },
  { code: SystemRoles.HealthcareAssistant, display: ROLE_DISPLAY_NAMES[SystemRoles.HealthcareAssistant] },
  { code: SystemRoles.LabTechnician, display: ROLE_DISPLAY_NAMES[SystemRoles.LabTechnician] },
  { code: SystemRoles.Pharmacist, display: ROLE_DISPLAY_NAMES[SystemRoles.Pharmacist] },
  { code: SystemRoles.HospitalAdmin, display: ROLE_DISPLAY_NAMES[SystemRoles.HospitalAdmin] },
  { code: SystemRoles.HealthcareAdmin, display: ROLE_DISPLAY_NAMES[SystemRoles.HealthcareAdmin] },
  { code: SystemRoles.Cashier, display: ROLE_DISPLAY_NAMES[SystemRoles.Cashier] },
  { code: SystemRoles.RemoteDoctor, display: ROLE_DISPLAY_NAMES[SystemRoles.RemoteDoctor] },
  { code: SystemRoles.PrimaryHealthcareProvider, display: ROLE_DISPLAY_NAMES[SystemRoles.PrimaryHealthcareProvider] },
  { code: SystemRoles.NurseMidwife, display: ROLE_DISPLAY_NAMES[SystemRoles.NurseMidwife] },
  { code: SystemRoles.EventManager, display: ROLE_DISPLAY_NAMES[SystemRoles.EventManager] }
];

/**
 * Type guard that narrows RoleType to PractitionerRoleType
 */
export function asPractitionerRole(role: RoleType): PractitionerRoleType {
  if (isPractitionerRole(role)) {
    return role as PractitionerRoleType;
  }
  throw new Error(`Role '${role}' is not a practitioner role`);
}

/**
 * Safe conversion with null return instead of throwing
 */
export function toPractitionerRole(role: RoleType): PractitionerRoleType | null {
  return isPractitionerRole(role) ? (role as PractitionerRoleType) : null;
}
